/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Navbar, NavbarBrand, NavbarText } from 'reactstrap'
import { FormattedHTMLMessage } from 'react-intl'
import { BsFileEarmarkPdfFill } from 'react-icons/bs'

import FormattedMessage from '../../components/FormattedMessage/FormattedMessage'

import LoginModal from '../../components/Modal/LoginModal/LoginModal'
import CreateAccountModal from '../../components/Modal/CreateAccountModal/CreateAccountModal'
import MyRewardsErrorModal from '../../components/Modal/MyRewardsErrorModal/MyRewardsErrorModal'
import TermsModal from '../../components/Modal/TermsModal/TermsModal'
import NoSpecialPromotion from './NoSpecialPromotion'

import { specialPromotionActions } from '../../redux/actions'

import './SpecialPromotionV2.scss'

function SpecialPromotionContainer(props) {
  const { promotion } = props
  const { pageUrl } = promotion
  const termsId = promotion.promotionTermId

  const [step, setStep] = useState(1)
  const [termsModalOpen, setTermsModalOpen] = useState(false)
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [createAccountModalOpen, setCreateAccountModalOpen] = useState(false)
  const [isError, setIsError] = useState(false)
  const terms = useSelector(state => state.specialPromotionReducer.terms)
  const currentUser = useSelector(state => state.userReducers.currentUser)
  const validSession = useSelector(state => state.userReducers.validSession)
  const promotionErrorString = useSelector(state => state.specialPromotionReducer.errorString)
  const isRedeemedSuccess = useSelector(state => state.specialPromotionReducer.isRedeemedSuccess)

  const dispatch = useDispatch()

  useEffect(() => {
    if (promotionErrorString) {
      setErrorModalOpen(true)
      setIsError(true)
    }
    return () => {
      setErrorModalOpen(false)
      setIsError(false)
    }
  }, [promotionErrorString, isError])

  useEffect(() => {
    if (currentUser && validSession) {
      setLoginModalOpen(false)
      setCreateAccountModalOpen(false)
    }
  }, [currentUser, validSession])

  useEffect(() => {
    if (isRedeemedSuccess) {
      setStep(2)
    }
  }, [isRedeemedSuccess])

  const handleAcceptOffer = e => {
    setIsError(false)
    e.preventDefault()
    if (!currentUser || !validSession) {
      toggleLoginModal()
    } else {
      // activate
      dispatch(specialPromotionActions.redeemPromotion({ pageUrl }))
    }
  }

  const toggleTermsModal = () => {
    setTermsModalOpen(!termsModalOpen)
  }

  const toggleErrorModal = () => {
    setErrorModalOpen(!errorModalOpen)
  }

  const toggleLoginModal = () => {
    setLoginModalOpen(!loginModalOpen)
  }

  const toggleCreateAccountModal = () => {
    setCreateAccountModalOpen(!createAccountModalOpen)
  }

  const handleTermsClick = () => {
    toggleTermsModal()
  }

  useEffect(() => {
    if (pageUrl) {
      dispatch(specialPromotionActions.getPromotion({ pageUrl }))
    }
  }, [pageUrl])

  useEffect(() => {
    if (termsId) {
      dispatch(specialPromotionActions.getTerms({ termsId }))
    }
  }, [termsId])

  if (!promotion) return null

  if (promotion && !promotion.active) {
    return <NoSpecialPromotion />
  }

  return (
    <div className="sp-page" role="main" aria-label="Special Promotion Main Section" tabIndex={-1}>
      <Promotion
        step={step}
        provider={promotion.provider}
        promotionImgName={promotion.promotionImgName}
        productImgName={promotion.productImgName}
      />
      <StickyFooter
        handleAcceptOffer={e => handleAcceptOffer(e)}
        step={step}
        handleTermsClick={() => handleTermsClick()}
        provider={promotion.provider}
      />
      <LoginModal isOpen={loginModalOpen} toggle={toggleLoginModal} aria-live="polite" />
      <CreateAccountModal
        isOpen={createAccountModalOpen}
        toggle={toggleCreateAccountModal}
        aria-live="polite"
      />
      <MyRewardsErrorModal
        isOpen={errorModalOpen}
        toggle={toggleErrorModal}
        error={promotionErrorString}
        aria-live="assertive"
      />
      {terms && (
        <TermsModal
          isOpen={termsModalOpen}
          toggle={toggleTermsModal}
          current={terms}
          aria-live="polite"
        />
      )}
      <br />
      <br />
    </div>
  )
}

function Promotion(props) {
  const { step, provider, promotionImgName, productImgName } = props

  switch (step) {
    case 1: {
      return (
        <PromotionSummary
          provider={provider}
          promotionImgName={promotionImgName}
          productImgName={productImgName}
        />
      )
    }
    case 2: {
      return <SuccessSummary />
    }
    default: {
      return (
        <PromotionSummary
          provider={provider}
          promotionImgName={promotionImgName}
          productImgName={productImgName}
        />
      )
    }
  }
}

function PromotionSummary(props) {
  const { provider, promotionImgName, productImgName } = props
  const [learnMore, setLearnMore] = useState(false)

  return (
    <div className="container mt-5 mb-10">
      <div className="row">
        <div className="col-md-12">
          <h1 className="page-title" aria-level="1">
            <FormattedMessage id={`page.giveawayPromotionPage.${provider}.heroTitle`} />
          </h1>
        </div>
        <div className="col-md-12">
          <h4 aria-level="2">
            <FormattedMessage id="global.label.specialPromotionGiveaway" />
          </h4>
        </div>
        <div className="col-md-12">
          <p>
            <FormattedHTMLMessage
              className="text-left"
              id={`page.giveawayPromotionPage.${provider}.description`}
            />
          </p>
        </div>
        <div className="col-md-12 mt-5">
          <h4 aria-level="2">
            <FormattedMessage id={`page.giveawayPromotionPage.${provider}.productSlogan`} />
          </h4>
        </div>
      </div>
      <div className="row">
        <img
          src={promotionImgName}
          alt="Special promotion banner"
          className="img-fluid promotion-banner"
          style={{ width: '100%' }}
        />
      </div>
      <div className="row product-banner-container">
        <div className="col-md-4 product-banner-left">
          <img src={productImgName} alt="Product banner" className="img-fluid" />
        </div>
        <div className="col-md-8 product-banner-right">
          {!learnMore ? (
            <div>
              <h1 className="page-title" aria-level="1">
                <FormattedMessage id={`page.giveawayPromotionPage.${provider}.productTitle`} />
              </h1>
              <p>
                <FormattedHTMLMessage
                  id={`page.giveawayPromotionPage.${provider}.productDescription1`}
                />
              </p>
              <Button
                color="light"
                outline
                onClick={() => setLearnMore(!learnMore)}
                aria-label="Learn more about this product"
              >
                <FormattedMessage id="global.label.learnMore" />
              </Button>
            </div>
          ) : (
            <div>
              <h1 className="page-title" aria-level="1">
                <FormattedMessage id={`page.giveawayPromotionPage.${provider}.productTitle`} />
              </h1>
              <p>
                <FormattedHTMLMessage
                  id={`page.giveawayPromotionPage.${provider}.productDescription2`}
                />
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StickyFooter(props) {
  const { handleAcceptOffer, handleTermsClick, step, provider } = props

  if (step !== 1) {
    return null
  }

  return (
    <div className="sticky-footer-container">
      <Navbar color="warning" fixed="bottom" role="contentinfo" aria-label="Promotion Footer">
        <NavbarBrand className="sticky-footer-link" tabIndex={0} aria-label="Terms and Conditions">
          <div
            onClick={handleTermsClick}
            tabIndex={0}
            aria-label="Open terms and conditions"
            role="button"
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') handleTermsClick()
            }}
            style={{ cursor: 'pointer' }}
          >
            <BsFileEarmarkPdfFill title="PDF" aria-hidden="true" focusable="false" />{' '}
            <FormattedMessage id="page.termsPage.title.heroTitle" /> (
            <FormattedMessage id={`page.giveawayPromotionPage.${provider}.heroTitle`} />)
          </div>
        </NavbarBrand>
        <NavbarText>
          <Button color="dark" onClick={e => handleAcceptOffer(e)} aria-label="Accept Offer">
            <FormattedMessage id="global.label.acceptOffer" />
          </Button>
        </NavbarText>
      </Navbar>
    </div>
  )
}

function SuccessSummary() {
  const handleViewMyRewards = () => {
    window.location.href = '/my-rewards'
  }

  return (
    <div className="container mt-5">
      <div className="row text-center">
        <div className="col-md-12">
          <h4 aria-level="2">
            <FormattedMessage id="global.label.offerClaim" />!
          </h4>
        </div>
        <div className="col-md-12">
          <p>
            <FormattedMessage id="global.label.seeReward" />
          </p>
        </div>
        <div className="col-md-12">
          <Button onClick={handleViewMyRewards} color="dark" aria-label="Continue to Rewards">
            <FormattedMessage id="global.label.continueReward" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SpecialPromotionContainer
